import Service from '@ember/service';
import { getOwner } from '@ember/application';
import { set, get } from '@ember/object';

/**
 * Cross-component application state, extracted from the global
 * `controller:application` coupling that the Classic mixins (base-beebee,
 * component-edit, new-model, ember-index, base-calendar) reach by name.
 *
 * Mixin-removal Phase 0: this service is an ADAPTER. It owns no state of its
 * own yet — every member delegates to the live application controller so the
 * behavior is byte-for-byte identical to reading `applicationController.*`
 * directly. Later phases migrate readers onto this service and then move the
 * state to live here. Keeping it a pure pass-through now means adding it
 * changes nothing observable; the suite must stay green.
 *
 * @class AppStateService
 * @extends Service
 */
class AppStateService extends Service {
  /**
   * The singleton application controller. Looked up lazily so the service can
   * be instantiated in contexts where the controller is not yet resolved.
   * @type {Controller}
   */
  get applicationController() {
    return getOwner(this).lookup('controller:application');
  }

  // --------------------------------------------------------------------------
  // State surface (delegates to the application controller)
  // --------------------------------------------------------------------------

  get modalComponents() {
    return this.applicationController?.modalComponents;
  }
  set modalComponents(value) {
    set(this.applicationController, 'modalComponents', value);
  }
  get indexLoaded() {
    return this.applicationController?.indexLoaded;
  }
  set indexLoaded(value) {
    set(this.applicationController, 'indexLoaded', value);
  }
  get reloadIndex() {
    return this.applicationController?.reloadIndex;
  }
  set reloadIndex(value) {
    set(this.applicationController, 'reloadIndex', value);
  }
  get confirmRemoveItem() {
    return this.applicationController?.confirmRemoveItem;
  }
  set confirmRemoveItem(value) {
    set(this.applicationController, 'confirmRemoveItem', value);
  }
  get openCards() {
    return this.applicationController?.openCards;
  }
  set openCards(value) {
    set(this.applicationController, 'openCards', value);
  }
  get itemPreview() {
    return this.applicationController?.itemPreview;
  }
  set itemPreview(value) {
    set(this.applicationController, 'itemPreview', value);
  }
  get userToShow() {
    return this.applicationController?.userToShow;
  }
  set userToShow(value) {
    set(this.applicationController, 'userToShow', value);
  }
  get roomToShow() {
    return this.applicationController?.roomToShow;
  }
  set roomToShow(value) {
    set(this.applicationController, 'roomToShow', value);
  }
  get allRooms() {
    return this.applicationController?.allRooms;
  }
  set allRooms(value) {
    set(this.applicationController, 'allRooms', value);
  }
  get schedulerView() {
    return this.applicationController?.schedulerView;
  }
  set schedulerView(value) {
    set(this.applicationController, 'schedulerView', value);
  }
  get isSmallDevice() {
    return this.applicationController?.isSmallDevice;
  }
  get popper() {
    return this.applicationController?.popper;
  }
  set popper(value) {
    set(this.applicationController, 'popper', value);
  }
  get gdprField() {
    return this.applicationController?.gdprField;
  }
  set gdprField(value) {
    set(this.applicationController, 'gdprField', value);
  }
  get isClinic() {
    return this.applicationController?.isClinic;
  }

  // --------------------------------------------------------------------------
  // The close-card task (ember-concurrency) lives on the controller
  // --------------------------------------------------------------------------

  get closeCardTask() {
    return this.applicationController?.closeCardTask;
  }

  /**
   * Close an open card via the controller's close-card task.
   * @param {*} item
   * @returns {TaskInstance}
   */
  closeCard(item) {
    return this.applicationController?.closeCardTask?.perform(item);
  }

  // --------------------------------------------------------------------------
  // Behavior surface (delegates to the application controller functions,
  // preserving `this` = the controller)
  // --------------------------------------------------------------------------

  saveRecord(...args) {
    return this.applicationController?.saveRecord(...args);
  }
  saveRecordRelated(...args) {
    return this.applicationController?.saveRecordRelated(...args);
  }
  destroyRecord(...args) {
    return this.applicationController?.destroyRecord(...args);
  }
  findError(...args) {
    return this.applicationController?.findError(...args);
  }
  myTransitionToRoute(...args) {
    return this.applicationController?.myTransitionToRoute(...args);
  }
  myTransitionToView(...args) {
    return this.applicationController?.myTransitionToView(...args);
  }

  /**
   * Read an arbitrary path off the application controller. Escape hatch for
   * the dynamically-set keys (`indexLoaded.<model>.<status>` etc.) that the
   * index/edit flows reach by string path today.
   * @param {string} path
   */
  getPath(path) {
    return get(this.applicationController, path);
  }

  /**
   * Set an arbitrary path on the application controller. Mirror of getPath.
   * @param {string} path
   * @param {*} value
   */
  setPath(path, value) {
    return set(this.applicationController, path, value);
  }
}

export { AppStateService as default };
//# sourceMappingURL=app-state.js.map
